import { CreditCardForm } from '../CreditCardForm';

import {
	Bank,
	Barcode,
	CalendarCheck,
	Clock,
	CreditCard as CreditCardIcon,
	Lightning,
	PixLogo,
	QrCode,
	ShieldCheck
} from '@phosphor-icons/react';
// Feather: traço fino de 2px — o Font Awesome ficava pesado na aba.

import { Row, Col, Tabs, Tab, Card } from 'react-bootstrap';
import { PlanContext } from '../Context/PlanContext';
import { ContentContext } from '../Context/ContentContext';
import { useContext, useEffect, useState } from 'react';
import { trackBehavior } from '../PageBlocks/behavior-tracker';
import { BillingType } from '../constants/billingType';
import { CURSEDUCA_PLANS_REFERENCES } from '../constants/plansReferences';

type BillingTypeFormProps = {
	setBillingType: (value: BillingType) => void;
};

export const BillingTypeForm = ({ setBillingType }: BillingTypeFormProps) => {
	const queryParams = new URLSearchParams(window.location.search);

	const [method, setMethod] = useState(queryParams.get('method') || '');

	const [onlyOnePaymentMethod, setOnlyOnePaymentMethod] = useState(false);

	const { plan } = useContext(PlanContext);
	const { content } = useContext(ContentContext);

	// Pix on subscriptions only exists on the Vindi gateway, and api/index.ts
	// routes to Vindi from this same map.
	const isPixAvailableForPlan =
		!!plan?.isPixEnabled && !!CURSEDUCA_PLANS_REFERENCES[plan.reference];

	// Pix Automático is the default: the payer authorizes once in their bank app
	// and later cycles are debited without action. `?pix_mode=manual` falls back
	// to the QR code flow.
	const pixBillingType =
		queryParams.get('pix_mode') === 'manual'
			? BillingType.Pix
			: BillingType.PixAutomatic;

	useEffect(() => {
		// A plan accepts either Pix or credit card, never both.
		if (plan) return setOnlyOnePaymentMethod(true);

		const isOnlyOnePaymentMethodAvailable = [
			content?.pricing.isCreditCardEnabled,
			content?.pricing.isPixEnabled,
			content?.pricing.isTicketEnabled
		];

		setOnlyOnePaymentMethod(
			isOnlyOnePaymentMethodAvailable.filter(Boolean).length === 1
		);
	}, [content, plan, isPixAvailableForPlan]);

	useEffect(() => {
		const isPix = method === 'pix';
		const isBoleto = method === 'boleto';

		// This effect used to bail out without content, so the plan flow never
		// resolved a method and stayed on credit card.
		if (plan) {
			return setBillingType(
				isPixAvailableForPlan ? pixBillingType : BillingType.CreditCard
			);
		}

		if (!content) return;

		if (content?.pricing.isCreditCardEnabled && !isPix && !isBoleto)
			return setBillingType(BillingType.CreditCard);

		if (content?.pricing.isTicketEnabled && !isPix)
			return setBillingType(BillingType.Boleto);

		if (content?.pricing.isPixEnabled)
			return setBillingType(BillingType.Pix);
	}, [content, plan, method, isPixAvailableForPlan, pixBillingType]);

	// A aba ativa tem que existir. Sem `?method=` o padrão era 'cc' sempre: produto só com Pix
	// (ou só boleto) abria "Método de pagamento" VAZIO — a aba de cartão não é renderizada e,
	// com um método só, as abas ficam ocultas (`d-none`), então não havia como clicar.
	const firstEnabledMethod = content?.pricing.isCreditCardEnabled
		? 'cc'
		: content?.pricing.isPixEnabled
		? 'pix'
		: content?.pricing.isTicketEnabled
		? 'boleto'
		: 'cc';
	// Com cartão como ÚNICA opção não há o que escolher: as abas são escondidas
	// (`d-none`) e o próprio formulário já se apresenta com "Dados do cartão de
	// crédito" — o título "Método de pagamento" fica solto em cima dele. Pix e
	// boleto continuam com o título: o painel deles não tem cabeçalho próprio.
	const onlyCreditCard =
		onlyOnePaymentMethod &&
		!isPixAvailableForPlan &&
		!content?.pricing.isPixEnabled &&
		!content?.pricing.isTicketEnabled;

	const activeKey = isPixAvailableForPlan
		? 'pix'
		: method || firstEnabledMethod;

	return (
		<>
			{!onlyCreditCard && (
				<Card.Title className="card-title mt-5 mt-md-8">
					<h3 className="card-label h4 text-gray-800">
						Método de pagamento
					</h3>
				</Card.Title>
			)}

			{/* The tab labels are hidden with a single method, so nothing else
			    would tell the buyer how they are paying. */}
			{isPixAvailableForPlan && (
				<div className="d-flex align-items-center mb-5">
					<span className="svg-icon svg-icon-primary svg-icon-2x mr-4">
						<PixLogo />
					</span>
					<div>
						<strong className="d-block text-gray-800">Pix</strong>
						<span className="text-gray-600 font-size-sm">
							Ao finalizar, você recebe o QR Code para pagar. Seu
							acesso é liberado assim que o pagamento for
							confirmado.
						</span>
					</div>
				</div>
			)}
			<Tabs
				variant="tabs"
				defaultActiveKey={activeKey}
				activeKey={activeKey}
				onSelect={(key) => {
					setMethod(key || 'cc');
					trackBehavior('method_selected', undefined, key || 'cc');
				}}
				className="d-flex justify-content-between mx-0 mb-3 nav-bordered"
			>
				{(content?.pricing.isCreditCardEnabled ||
					(plan && !isPixAvailableForPlan)) && (
					<Tab
						title={
							<div className="payment-tab-title">
								<CreditCardIcon
									className="payment-tab-icon"
									aria-hidden
								/>
								<span className="nav-text">Cartão</span>
							</div>
						}
						eventKey="cc"
						tabClassName={`col ${
							onlyOnePaymentMethod && 'd-none'
						} ${
							(content?.pricing.isTicketEnabled ||
								content?.pricing.isPixEnabled ||
								isPixAvailableForPlan) &&
							'mr-5'
						} `}
						onEnter={() => setBillingType(BillingType.CreditCard)}
					>
						<CreditCardForm />
					</Tab>
				)}
				{(content?.pricing.isPixEnabled || isPixAvailableForPlan) && (
					<Tab
						title={
							<div className="payment-tab-title">
								<PixLogo className="payment-tab-icon" />
								<span className="nav-text">Pix</span>
							</div>
						}
						eventKey="pix"
						tabClassName={`col ml-3 ${
							onlyOnePaymentMethod && 'd-none'
						}`}
						onEnter={() => setBillingType(BillingType.Pix)}
					>
						<div className="payment-method-info">
							<div className="payment-info-item">
								<span className="payment-info-icon">
									<Lightning aria-hidden />
								</span>
								<div>
									<b>Aprovação na hora</b>
									<span>
										O pagamento é confirmado em segundos,
										sem custo adicional
									</span>
								</div>
							</div>
							<div className="payment-info-item">
								<span className="payment-info-icon">
									<QrCode aria-hidden />
								</span>
								<div>
									<b>QR Code ou copia e cola</b>
									<span>
										Abra o app do seu banco e escaneie o
										código gerado
									</span>
								</div>
							</div>
							<div className="payment-info-item">
								<span className="payment-info-icon">
									<ShieldCheck aria-hidden />
								</span>
								<div>
									<b>Seguro</b>
									<span>
										Tecnologia oficial do Banco Central do
										Brasil
									</span>
								</div>
							</div>
						</div>
					</Tab>
				)}
				{content?.pricing.isTicketEnabled && (
					<Tab
						title={
							<div className="payment-tab-title">
								<Barcode
									className="payment-tab-icon"
									aria-hidden
								/>
								<span className="nav-text">Boleto</span>
							</div>
						}
						eventKey="boleto"
						tabClassName={`col mx-3 ${
							onlyOnePaymentMethod && 'd-none'
						}`}
						onEnter={() => setBillingType(BillingType.Boleto)}
					>
						<div className="payment-method-info">
							<div className="payment-info-item">
								<span className="payment-info-icon">
									<Clock aria-hidden />
								</span>
								<div>
									<b>Compensação em até 3 dias úteis</b>
									<span>
										O acesso é liberado assim que o banco
										confirmar o pagamento
									</span>
								</div>
							</div>
							<div className="payment-info-item">
								<span className="payment-info-icon">
									<CalendarCheck aria-hidden />
								</span>
								<div>
									<b>Atenção ao vencimento</b>
									<span>
										Pague até a data limite para garantir
										sua compra
									</span>
								</div>
							</div>
							<div className="payment-info-item">
								<span className="payment-info-icon">
									<Bank aria-hidden />
								</span>
								<div>
									<b>Pague onde preferir</b>
									<span>
										Qualquer banco, app ou casa lotérica
									</span>
								</div>
							</div>
						</div>
					</Tab>
				)}
			</Tabs>
		</>
	);
};
